import { component } from "picoapp";
import { TRANSITION_PRODUCT_VIEW_AFTER } from "../../Barba/Views/constants";
import { OPTION_SELECTION } from "../../StayAI/oli-subscription-box";
import { formatMoney } from "../../../utilities/general";

type StickyGaEvent = (e: MouseEvent) => void;
type StickyPositionClasses = "top" | "bottom";
type StickyOptionSelectionDetail = {
  subscriptionPlanId: string;
  frequencyText?: string;
};

const STICKY_ADD_TO_CART_UPDATE = "oli:sticky-add-to-cart:update";

/** PDP flavor links carry the same price data-* as the sticky root; copy on Barba so JS stays self-contained. */
const copyStickyPriceDataset = (from: HTMLElement, to: HTMLElement) => {
  const keys = [
    "originalOneTimePrice",
    "oneTimePrice",
    "originalSellingPlanPrice",
    "sellingPlanPrice",
  ] as const;
  keys.forEach((key) => {
    const val = from.dataset[key];
    if (val !== undefined) {
      to.dataset[key] = val;
    }
  });
};

const GAEvent = (productTitle: string) => () => {
  if (window.gtag) {
    window.gtag("event", "Sticky Add to Cart", {
      event_detail: productTitle,
    });
  }
};

const REBUY_SMARTCART_SHOW = "rebuy:smartcart.show";
const REBUY_SMARTCART_HIDE = "rebuy:smartcart.hide";
const REBUY_CART_OPEN_ATTR = "data-rebuy-smartcart-open";

export const stickyAddToCart = component((node: HTMLElement) => {
  let gaEvent: StickyGaEvent;
  let observer: IntersectionObserver;
  let optionSelectionHandler: ((event: Event) => void) | undefined;
  let stickyActivationFrame: number | undefined;

  const getProductForm = () =>
    document.querySelector<HTMLFormElement>("#oli-product-form");

  const getStickySubmitButton = () =>
    node.querySelector<HTMLButtonElement>("button[type='submit']") ||
    node.querySelector<HTMLButtonElement>("button");

  const updateStickyPricing = (detail?: StickyOptionSelectionDetail) => {
    const productPriceEl = node.querySelector<HTMLElement>("[data-price]");
    const compareAtPriceEl = node.querySelector<HTMLElement>(
      "[data-compare-at-price]"
    );
    const deliveryText = node.querySelector<HTMLElement>("[data-delivery-text]");

    if (!productPriceEl || !compareAtPriceEl || !deliveryText) {
      return;
    }

    const originalOneTimePrice = Number(
      node.dataset.originalOneTimePrice || 0
    );
    const oneTimePrice = Number(node.dataset.oneTimePrice || 0);
    const originalSellingPlanPrice = Number(
      node.dataset.originalSellingPlanPrice || 0
    );
    const sellingPlanPrice = Number(node.dataset.sellingPlanPrice || 0);

    if (!detail || (detail.subscriptionPlanId === "" && !detail.frequencyText)) {
      deliveryText.innerHTML = "One Time";
      productPriceEl.innerHTML = formatMoney(oneTimePrice);

      if (originalOneTimePrice > oneTimePrice) {
        compareAtPriceEl.innerHTML = formatMoney(originalOneTimePrice);
        compareAtPriceEl.classList.remove("tw-hidden");
      } else {
        compareAtPriceEl.classList.add("tw-hidden");
      }

      return;
    }

    deliveryText.innerHTML = (detail.frequencyText || "").toLowerCase();
    productPriceEl.innerHTML = formatMoney(sellingPlanPrice);

    if (originalSellingPlanPrice > sellingPlanPrice) {
      compareAtPriceEl.innerHTML = formatMoney(originalSellingPlanPrice);
      compareAtPriceEl.classList.remove("tw-hidden");
    } else {
      compareAtPriceEl.classList.add("tw-hidden");
    }
  };

  const bindOptionSelection = () => {
    if (optionSelectionHandler) {
      window.removeEventListener(OPTION_SELECTION, optionSelectionHandler);
    }

    optionSelectionHandler = (event: Event) => {
      updateStickyPricing(
        (event as CustomEvent<StickyOptionSelectionDetail>).detail
      );
    };

    window.addEventListener(OPTION_SELECTION, optionSelectionHandler);
  };

  const replaceStickyMarkup = (markup: string) => {
    const stickyDocument = new DOMParser().parseFromString(markup, "text/html");
    const sourceStickyNode = stickyDocument.querySelector<HTMLElement>(
      "[data-sticky-add-to-cart]"
    );

    if (!sourceStickyNode) return;

    node.className = sourceStickyNode.className;
    node.innerHTML = sourceStickyNode.innerHTML;

    const sourceStickyStyle = sourceStickyNode.getAttribute("style");

    if (sourceStickyStyle) {
      node.setAttribute("style", sourceStickyStyle);
    } else {
      node.removeAttribute("style");
    }

    Object.entries(sourceStickyNode.dataset).forEach(([key, value]) => {
      node.dataset[key] = value;
    });

    createSticky();
  };

  window.addEventListener(TRANSITION_PRODUCT_VIEW_AFTER, ({ detail }) => {
    const trigger = detail?.trigger as HTMLElement | undefined;
    if (trigger) {
      copyStickyPriceDataset(trigger, node);
    }
    const newProductTitle = trigger?.dataset?.title;
    const productAvailability = trigger?.dataset?.productAvailable;
    if (observer) {
      observer.disconnect();
    }
    createSticky(newProductTitle, productAvailability);
  });

  const createSticky = (
    productTitle?: string,
    productAvailability?: string
  ) => {
    const stickyAddToCartButton = getStickySubmitButton();
    const productForm = getProductForm();
    const submitButton = productForm?.querySelector<HTMLElement>(
      "button[type='submit']"
    );
    const productTitleEl = node.querySelector<HTMLElement>(
      "[data-product-title]"
    );
    const productAvailableText = node.querySelector<HTMLElement>(
      "[data-product-available-text]"
    );
    const productOOSText = node.querySelector<HTMLElement>("[data-oos-text]");
    const stickyOOSButton = node.querySelector<HTMLElement>("[data-oos-cta]");
    const stickyAvailability = productAvailability ?? node.dataset.productAvailable;
    const anchorOOSElement = productForm?.querySelector<HTMLButtonElement>(
      "button[data-action='back-in-stock']"
    );

    if (
      !stickyAddToCartButton ||
      !productAvailableText ||
      !productOOSText ||
      !stickyOOSButton
    ) {
      return;
    }

    // Handle OOS State
    if (stickyAvailability === "false") {
      toggleStickyButton(false);
    } else {
      toggleStickyButton(true);
    }

    function toggleStickyButton(show: boolean) {
      if (show) {
        stickyAddToCartButton.classList.remove("tw-hidden");
        productAvailableText.classList.remove("tw-hidden");
        productOOSText.classList.add("tw-hidden");
        stickyOOSButton.classList.add("tw-hidden");
        productOOSText.classList.remove("tw-block");
        stickyOOSButton.classList.remove("tw-block");
      } else {
        stickyAddToCartButton.classList.add("tw-hidden");
        productAvailableText.classList.add("tw-hidden");
        productOOSText.classList.remove("tw-hidden");
        stickyOOSButton.classList.remove("tw-hidden");
        productOOSText.classList.add("tw-block");
        stickyOOSButton.classList.add("tw-block");
      }
    }

    if (productTitle && productTitleEl) {
      productTitleEl.innerHTML = productTitle;
    }

    const observedAnchor =
      stickyAvailability === "false" ? anchorOOSElement : submitButton;

    if (!productForm || !observedAnchor) return;

    if (observer) {
      observer.disconnect();
    }

    function showSticky(position: StickyPositionClasses) {
      if (node.hasAttribute("data-active")) {
        return;
      }

      if (stickyActivationFrame) {
        window.cancelAnimationFrame(stickyActivationFrame);
      }

      node.setAttribute("data-sticky-preparing", "true");
      node.removeAttribute("data-active");
      stickyRenderEffects(position);

      // Commit the hidden top/bottom position before enabling the slide-in transition.
      node.getBoundingClientRect();

      stickyActivationFrame = window.requestAnimationFrame(() => {
        node.removeAttribute("data-sticky-preparing");

        // Recalculate with transitions re-enabled, then start the slide.
        node.getBoundingClientRect();

        stickyActivationFrame = window.requestAnimationFrame(() => {
          node.setAttribute("data-active", "true");
          stickyActivationFrame = undefined;
        });
      });
    }

    function hideSticky() {
      if (stickyActivationFrame) {
        window.cancelAnimationFrame(stickyActivationFrame);
        stickyActivationFrame = undefined;
      }

      node.removeAttribute("data-sticky-preparing");
      node.removeAttribute("data-active");
      stickyRenderEffects();
    }

    function handleIntersection(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (window.innerWidth <= 768) {
          if (!entry.isIntersecting) {
            showSticky("bottom");
          } else {
            hideSticky();
          }
        } else {
          if (!entry.rootBounds) return;

          const scrollPosition =
            document.documentElement.scrollTop || document.body.scrollTop;
          const anchorTop = entry.boundingClientRect.top;

          const headerHeight =
            document.querySelector<HTMLElement>("#shopify-section-header")
              ?.offsetHeight || 0;

          if (entry.isIntersecting) {
            hideSticky();
          } else if (!entry.isIntersecting && scrollPosition < anchorTop) {
            node.style.bottom = "0px";
            node.style.top = "unset";
            showSticky("bottom");
          } else if (!entry.isIntersecting && scrollPosition > anchorTop) {
            node.style.top = headerHeight.toString() + 'px';
            node.style.bottom = "unset";
            showSticky("top");
          }
        }
      });
    }

    observer = new IntersectionObserver(handleIntersection);
    observer.observe(observedAnchor);
    updateStickyPricing();
    bindOptionSelection();
    handleGAEvent(productTitleEl?.innerHTML || "");
  };

  createSticky();

  const onRebuySmartCartShow = () => {
    node.setAttribute(REBUY_CART_OPEN_ATTR, "");
    document.body.classList.remove("sticky-cta-top", "sticky-cta-bottom");
  };

  const onRebuySmartCartHide = () => {
    node.removeAttribute(REBUY_CART_OPEN_ATTR);
  };

  document.addEventListener(REBUY_SMARTCART_SHOW, onRebuySmartCartShow);
  document.addEventListener(REBUY_SMARTCART_HIDE, onRebuySmartCartHide);

  window.addEventListener(STICKY_ADD_TO_CART_UPDATE, (event: Event) => {
    replaceStickyMarkup(
      (event as CustomEvent<{ markup: string }>).detail.markup
    );
  });

  function stickyRenderEffects(position?: StickyPositionClasses) {
    const productForm = document.querySelector<HTMLElement>("#product-form-content");
    if (position) {
      node.dataset.stickyPosition = position;
    }

    setTimeout(() => {
      document.body.classList.remove("sticky-cta-top", "sticky-cta-bottom");
      if (position && productForm && !productForm.classList.contains("hide")) {
        document.body.classList.add(`sticky-cta-${position}`);
      }
    }, 100);
  }

  function handleGAEvent(productTitle: string) {
    const stickyAddToCartButton = getStickySubmitButton();

    if (!stickyAddToCartButton) return;

    if (gaEvent) {
      stickyAddToCartButton.removeEventListener("click", gaEvent);
    }
    gaEvent = GAEvent(productTitle);
    stickyAddToCartButton.addEventListener("click", gaEvent);
  }

  return () => {
    if (stickyActivationFrame) {
      window.cancelAnimationFrame(stickyActivationFrame);
    }
    node.removeAttribute("data-sticky-preparing");
    document.removeEventListener(REBUY_SMARTCART_SHOW, onRebuySmartCartShow);
    document.removeEventListener(REBUY_SMARTCART_HIDE, onRebuySmartCartHide);
    node.removeAttribute(REBUY_CART_OPEN_ATTR);
  };
});
